import React, { useId } from 'react';
import {
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
  type ValidationResult,
  Label as RACLabel,
  Input as RACInput,
} from 'react-aria-components';
import { Description, FieldError, Input, Label, type InputProps } from './field';
import { composeTailwindRenderProps } from './component-utils';

type DefaultTextFieldProps = {
  label: string;
  errorMessage?: string | ((validation: ValidationResult) => string) | undefined;
} & AriaTextFieldProps;

type NormalTextFieldProps = DefaultTextFieldProps & {
  variant?: 'normal';
  hiddenLabel?: boolean;
  description?: string;
  inputProps?: InputProps;
};

type BoxedTextFieldProps = DefaultTextFieldProps & {
  variant: 'box';
  label: string;
  placeholder?: string;
};

type TextFieldProps = NormalTextFieldProps | BoxedTextFieldProps;

export function TextField(props: TextFieldProps) {
  const id = useId();
  if (props.variant === 'box') {
    const { placeholder = ' ', className, label, errorMessage, ...rest } = props;

    return (
      <AriaTextField {...rest} className={composeTailwindRenderProps(className, 'group relative w-full')}>
        <RACInput
          placeholder={placeholder}
          className="peer h-[52px] w-full border px-3 pt-1.5 text-sm outline-none placeholder:text-transparent invalid:border-error focus:border-dark focus:outline-none focus:invalid:border-error focus-visible:ring-2 focus-visible:ring-dark focus-visible:ring-opacity-60 forced-colors:outline-[Highlight]"
        />
        <RACLabel className="absolute bottom-[32px] left-3 text-xxs text-muted transition-all peer-placeholder-shown:bottom-[18px] peer-placeholder-shown:text-sm peer-focus:bottom-[32px] peer-focus:text-xxs">
          {label}
        </RACLabel>
        {errorMessage ?
          <FieldError className="absolute inset-x-0 top-full z-10 border border-t-0 border-error bg-clear px-2 py-1 text-center">{errorMessage}</FieldError>
        : <FieldError className="absolute inset-x-0 top-full z-10 border border-t-0 border-error bg-clear px-2 py-1 text-center">
            {({ validationErrors }) =>
              validationErrors.map((v, i) => (
                <span className="block" key={`${id}-${i}`}>
                  {v}
                </span>
              ))
            }
          </FieldError>
        }
      </AriaTextField>
    );
  }
  const { label, description, errorMessage, inputProps, hiddenLabel, className, ...rest } = props;

  return (
    <AriaTextField {...rest} className={composeTailwindRenderProps(className, 'flex flex-col gap-2')}>
      <Label className={hiddenLabel ? 'sr-only' : ''}>{label}</Label>
      <Input {...inputProps} />
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
    </AriaTextField>
  );
}
